import { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Building2, DollarSign, TrendingUp, FileText, Phone, Mail, MapPin, CreditCard } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { AnnouncementCard } from '@/components/Cards';
import { announcements, projectInfo } from '@/data/index';
import type { AnnouncementItem } from '@/lib/index';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

const categories = ['Tất cả', 'Thông báo dự án', 'Thông tin dự án', 'Giá bán', 'Tiến độ', 'Hồ sơ'] as const;

export default function Announcement() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<typeof categories[number]>('Tất cả');

  const filteredAnnouncements = announcements.filter((announcement) => {
    const matchesSearch = announcement.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         announcement.summary.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'Tất cả' || announcement.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categoryIcons: Record<string, typeof Building2> = {
    'Thông báo dự án': Building2,
    'Giá bán': DollarSign,
    'Tiến độ': TrendingUp,
    'Hồ sơ': FileText,
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <div className="relative bg-gradient-to-b from-primary/5 via-background to-background py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Công Bố Thông Tin
              </h1>
              <p className="text-lg text-muted-foreground">
                Thông tin minh bạch về dự án HARMONY Xuân Phương – OCT1 &amp; OCT3. Chủ đầu tư: Công ty CP Đầu tư Vietasset. Hotline: <strong>0917978466</strong>
              </p>
            </motion.div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Alert className="mb-8 border-primary/20 bg-primary/5">
              <FileText className="h-5 w-5 text-primary" />
              <AlertDescription className="text-base">
                <strong>Quy định pháp lý:</strong> Theo Nghị định 54/2026, chủ đầu tư phải công bố thông tin dự án trong vòng 30 ngày kể từ ngày khởi công xây dựng. Thông tin được cập nhật định kỳ và công khai minh bạch cho người dân.
              </AlertDescription>
            </Alert>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-8"
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      placeholder="Tìm kiếm thông báo..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Filter className="h-5 w-5 text-muted-foreground" />
                    <div className="flex flex-wrap gap-2">
                      {categories.map((category) => (
                        <Button
                          key={category}
                          variant={selectedCategory === category ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSelectedCategory(category)}
                        >
                          {category}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-semibold mb-6">Thông báo mới nhất</h2>
            {filteredAnnouncements.length > 0 ? (
              <div className="grid gap-6">
                {filteredAnnouncements.map((announcement, index) => (
                  <motion.div
                    key={announcement.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                  >
                    <AnnouncementCard announcement={announcement} />
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Không tìm thấy thông báo phù hợp</p>
                </CardContent>
              </Card>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-semibold mb-6">Thông tin chính dự án</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <Building2 className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Tổng số căn hộ</p>
                      <p className="text-2xl font-bold font-mono">{projectInfo.totalUnits}</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">OCT1: 240 căn (3.185m² đất) + OCT3: 578 căn (7.836m² đất)</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <DollarSign className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Giá bán</p>
                      <p className="text-2xl font-bold font-mono">Theo NOXH</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">Vay ưu đãi: 6,1-6,6%/năm – Vietcombank, MB Bank, UBSP</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <TrendingUp className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Bàn giao</p>
                      <p className="text-2xl font-bold font-mono">QIII/2028</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">Nhận hồ sơ từ QI/2026 – Cấp sổ hồng QI/2029</p>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Hỗ trợ vay vốn ngân hàng */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-semibold mb-2 flex items-center gap-2">
              <CreditCard className="h-6 w-6 text-primary" />
              Hỗ Trợ Tài Chính – Vay Ưu Đãi Mua NOXH
            </h2>
            <p className="text-muted-foreground mb-6">Chủ đầu tư Vietasset ký kết hợp tác với 3 ngân hàng lớn, cung cấp dịch vụ tư vấn "2 trong 1" ngay tại văn phòng giao dịch dự án</p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  bank: 'Ngân hàng UBSP',
                  rate: '6,6%/năm',
                  fixed: 'Lãi suất nợ quá hạn: 130%',
                  maxLoan: '80% giá trị căn hộ',
                  maxAmount: '≤ 1 tỷ đồng',
                  term: 'Lên đến 25 năm',
                  color: 'bg-blue-50 border-blue-200',
                },
                {
                  bank: 'Vietcombank',
                  rate: '6,1%/năm',
                  fixed: 'Cố định 5 năm đầu',
                  maxLoan: '70% giá trị căn hộ',
                  maxAmount: '≤ 1 tỷ đồng',
                  term: 'Lên đến 25 năm',
                  color: 'bg-green-50 border-green-200',
                },
                {
                  bank: 'MB Bank',
                  rate: '6,6%/năm',
                  fixed: 'Cố định 10 năm đầu',
                  maxLoan: '70% giá trị căn hộ',
                  maxAmount: '≤ 1 tỷ đồng',
                  term: 'Lên đến 25 năm',
                  color: 'bg-purple-50 border-purple-200',
                },
              ].map((item) => (
                <Card key={item.bank} className={`border ${item.color}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{item.bank}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Lãi suất ưu đãi:</span>
                      <span className="font-bold text-primary">{item.rate}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Ưu đãi:</span>
                      <span className="font-medium">{item.fixed}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Mức vay tối đa:</span>
                      <span className="font-medium">{item.maxLoan}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Giá trị giải ngân:</span>
                      <span className="font-medium">{item.maxAmount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Thời gian vay:</span>
                      <span className="font-medium">{item.term}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <Card className="bg-gradient-to-br from-primary/5 to-accent/5">
              <CardContent className="p-8">
                <h2 className="text-2xl font-semibold mb-6">Liên hệ để biết thêm thông tin</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Phone className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Hotline tư vấn</p>
                      <p className="text-muted-foreground font-bold">0917978466</p>
                      <p className="text-sm text-muted-foreground">8:00 - 17:30 (Thứ 2 - Thứ 7)</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Mail className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Email</p>
                      <p className="text-muted-foreground">info@vietasset.vn</p>
                      <p className="text-sm text-muted-foreground">Phản hồi trong 24h</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <MapPin className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Văn phòng tiếp nhận hồ sơ</p>
                      <p className="text-muted-foreground">Tầng 5, tòa CT1 The Pride Tố Hữu</p>
                      <p className="text-sm text-muted-foreground">Hà Đông, TP. Hà Nội</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}